// Paywall, subscription, and monetization analytics events

export type SubscriptionTier = 'free' | 'core' | 'premium' | 'professional';
export type ResourcePackType = 'strategy_boost' | 'document_pack' | 'export_pack' | 'full_refill';

export const paywallEvents = {
  shown: (
    feature: string,
    requiredTier: SubscriptionTier,
    currentTier: SubscriptionTier,
    sourcePage: string
  ) => ({
    event: 'paywall_shown',
    properties: {
      feature_blocked: feature,
      required_tier: requiredTier,
      current_tier: currentTier,
      source_page: sourcePage,
    },
  }),

  dismissed: (feature: string, timeVisibleMs: number) => ({
    event: 'paywall_dismissed',
    properties: {
      feature_blocked: feature,
      time_visible_ms: timeVisibleMs,
    },
  }),

  upgradeClicked: (
    feature: string,
    requiredTier: SubscriptionTier,
    currentTier: SubscriptionTier
  ) => ({
    event: 'paywall_upgrade_clicked',
    properties: {
      feature_blocked: feature,
      required_tier: requiredTier,
      current_tier: currentTier,
    },
  }),

  pricingPageViewed: (source: string, currentTier: SubscriptionTier) => ({
    event: 'pricing_page_viewed',
    properties: {
      source,
      current_tier: currentTier,
    },
  }),

  tierSelected: (tier: SubscriptionTier, billingCycle: 'monthly' | 'annual') => ({
    event: 'pricing_tier_selected',
    properties: {
      tier,
      billing_cycle: billingCycle,
    },
  }),

  tierCompared: (tiersCompared: SubscriptionTier[]) => ({
    event: 'pricing_tiers_compared',
    properties: {
      tiers_compared: tiersCompared,
    },
  }),

  checkoutStarted: (
    tier: SubscriptionTier,
    price: number,
    source: string,
    billingCycle: 'monthly' | 'annual' | 'one_time',
    pricingVariant?: string
  ) => ({
    event: 'checkout_started',
    properties: {
      tier,
      price,
      source,
      billing_cycle: billingCycle,
      currency: 'USD',
      ...(pricingVariant && { pricing_variant: pricingVariant }),
    },
  }),

  checkoutCompleted: (
    tier: SubscriptionTier,
    price: number,
    source: string,
    isUpgrade: boolean,
    pricingVariant?: string
  ) => ({
    event: 'checkout_completed',
    properties: {
      tier,
      price,
      source,
      is_upgrade: isUpgrade,
      currency: 'USD',
      ...(pricingVariant && { pricing_variant: pricingVariant }),
    },
  }),

  checkoutFailed: (tier: SubscriptionTier, error: string) => ({
    event: 'checkout_failed',
    properties: {
      tier,
      error_type: categorizeCheckoutError(error),
      error_message: error,
    },
  }),

  checkoutAbandoned: (tier: SubscriptionTier, stepReached: string) => ({
    event: 'checkout_abandoned',
    properties: {
      tier,
      step_reached: stepReached,
    },
  }),

  // Recovery email events
  recoveryEmailSent: (
    emailNumber: 1 | 2 | 3,
    tier: SubscriptionTier,
    hoursSinceAbandonment: number,
    checkoutIntentId: string
  ) => ({
    event: 'recovery_email_sent',
    properties: {
      email_number: emailNumber,
      tier,
      hours_since_abandonment: hoursSinceAbandonment,
      checkout_intent_id: checkoutIntentId,
    },
  }),

  recoveryEmailOpened: (
    emailNumber: 1 | 2 | 3,
    tier: SubscriptionTier,
    checkoutIntentId: string
  ) => ({
    event: 'recovery_email_opened',
    properties: {
      email_number: emailNumber,
      tier,
      checkout_intent_id: checkoutIntentId,
    },
  }),

  recoveryLinkClicked: (
    checkoutIntentId: string,
    tier: SubscriptionTier,
    emailNumber: 1 | 2 | 3
  ) => ({
    event: 'recovery_link_clicked',
    properties: {
      checkout_intent_id: checkoutIntentId,
      tier,
      email_number: emailNumber,
    },
  }),

  recoveryCompleted: (
    checkoutIntentId: string,
    tier: SubscriptionTier,
    emailNumber: 1 | 2 | 3,
    hoursSinceAbandonment: number,
    discountApplied: boolean = false
  ) => ({
    event: 'recovery_completed',
    properties: {
      checkout_intent_id: checkoutIntentId,
      tier,
      converting_email_number: emailNumber,
      hours_since_abandonment: hoursSinceAbandonment,
      discount_applied: discountApplied,
    },
  }),

  subscriptionCreated: (tier: SubscriptionTier, mrr: number) => ({
    event: 'subscription_created',
    properties: {
      tier,
      mrr,
      currency: 'USD',
    },
  }),

  subscriptionUpgraded: (fromTier: SubscriptionTier, toTier: SubscriptionTier, newMrr: number) => ({
    event: 'subscription_upgraded',
    properties: {
      from_tier: fromTier,
      to_tier: toTier,
      mrr: newMrr,
      currency: 'USD',
    },
  }),

  subscriptionDowngraded: (fromTier: SubscriptionTier, toTier: SubscriptionTier, reason?: string) => ({
    event: 'subscription_downgraded',
    properties: {
      from_tier: fromTier,
      to_tier: toTier,
      reason,
    },
  }),

  subscriptionCanceled: (
    tier: SubscriptionTier,
    daysActive: number,
    cancellationReason?: string
  ) => ({
    event: 'subscription_canceled',
    properties: {
      tier,
      days_active: daysActive,
      cancellation_reason: cancellationReason,
    },
  }),

  subscriptionReactivated: (tier: SubscriptionTier) => ({
    event: 'subscription_reactivated',
    properties: {
      tier,
    },
  }),

  trialStarted: (tier: SubscriptionTier, trialDays: number) => ({
    event: 'trial_started',
    properties: {
      tier,
      trial_days: trialDays,
    },
  }),

  trialEnding: (tier: SubscriptionTier, daysRemaining: number) => ({
    event: 'trial_ending',
    properties: {
      tier,
      days_remaining: daysRemaining,
    },
  }),

  trialConverted: (tier: SubscriptionTier) => ({
    event: 'trial_converted',
    properties: {
      tier,
    },
  }),

  trialExpired: (tier: SubscriptionTier) => ({
    event: 'trial_expired',
    properties: {
      tier,
    },
  }),

  resourcePackViewed: (packType: ResourcePackType, currentTier: SubscriptionTier) => ({
    event: 'resource_pack_viewed',
    properties: {
      pack_type: packType,
      current_tier: currentTier,
    },
  }),

  resourcePackPurchased: (
    packType: ResourcePackType,
    price: number,
    currentTier: SubscriptionTier
  ) => ({
    event: 'resource_pack_purchased',
    properties: {
      pack_type: packType,
      price,
      current_tier: currentTier,
      currency: 'USD',
    },
  }),

  usageLimitReached: (
    feature: string,
    currentUsage: number,
    limit: number,
    tier: SubscriptionTier
  ) => ({
    event: 'usage_limit_reached',
    properties: {
      feature,
      current_usage: currentUsage,
      limit,
      tier,
      usage_percentage: Math.round((currentUsage / limit) * 100),
    },
  }),

  usageLimitWarning: (
    feature: string,
    currentUsage: number,
    limit: number,
    tier: SubscriptionTier
  ) => ({
    event: 'usage_limit_warning',
    properties: {
      feature,
      current_usage: currentUsage,
      limit,
      tier,
      usage_percentage: Math.round((currentUsage / limit) * 100),
    },
  }),

  featureAccessDenied: (
    feature: string,
    requiredTier: SubscriptionTier,
    currentTier: SubscriptionTier,
    sourcePage: string
  ) => ({
    event: 'feature_access_denied',
    properties: {
      feature,
      required_tier: requiredTier,
      current_tier: currentTier,
      source_page: sourcePage,
    },
  }),
};

function categorizeCheckoutError(error: string): string {
  const lowerError = error.toLowerCase();
  if (lowerError.includes('card') || lowerError.includes('declined')) return 'card_declined';
  if (lowerError.includes('expired')) return 'card_expired';
  if (lowerError.includes('cvc') || lowerError.includes('cvv')) return 'invalid_cvc';
  if (lowerError.includes('fraud')) return 'fraud_suspected';
  if (lowerError.includes('insufficient')) return 'insufficient_funds';
  if (lowerError.includes('network') || lowerError.includes('connection')) return 'network_error';
  return 'unknown';
}

export type PaywallEvent = ReturnType<typeof paywallEvents[keyof typeof paywallEvents]>;
